// The report form, new and edit. The form is never closed or reloaded by a failed save: field errors show
// inline, a 409 offers Reload, and the user's work stays on screen until they choose otherwise.
import React, { useEffect, useMemo, useRef, useState } from "react";
import { Link, Prompt, useHistory, useParams } from "react-router-dom";
import { useSelector } from "react-redux";
import { useSnackbar } from "notistack";
import {
  Alert,
  Button,
  Card,
  CircularProgress,
  FormControl,
  Grid,
  InputLabel,
  MenuItem,
  Select,
  TextField,
} from "@mui/material";
import Loader from "components/Progress";
import { showSnackbar } from "Utilities/Snackbar";
import { useCreateReportMutation, useGetReportQuery, useUpdateReportMutation } from "services/scheduledReports";
import { classifyReportError, fieldError } from "utils/reportErrors";
import {
  applyPreset,
  buildBody,
  buildDraft,
  formFromReport,
  newForm,
  presetKey,
  presetLabel,
  sectionFeatures,
  solutionsOffered,
  validateForm,
} from "utils/reportDefinition";
import { browserZone } from "utils/timezone";
import PreviewPane from "./PreviewPane";
import RecipientList from "./RecipientList";
import SchedulePicker from "./SchedulePicker";
import ScopePicker from "./ScopePicker";
import SectionPicker from "./SectionPicker";

const LEAVE_MESSAGE = "You have unsaved changes on this report. Leave this page?";

function Block({ title, children }) {
  return (
    <Card style={{ padding: "16px", marginBottom: "16px" }}>
      <p style={{ fontSize: "15px", fontWeight: "bold", color: "#333333", margin: "0 0 12px 0", textTransform: "uppercase" }}>
        {title}
      </p>
      {children}
    </Card>
  );
}

// A choice from the configuration's lists. A stored value the list no longer has stays selectable.
function PresetSelect({ label, value, options, onChange }) {
  const known = options.some((o) => presetKey(o) === value);
  return (
    <FormControl size="small" fullWidth>
      <InputLabel>{label}</InputLabel>
      <Select label={label} value={value} onChange={(e) => onChange(e.target.value)}>
        <MenuItem value="">None</MenuItem>
        {options.map((o) => (
          <MenuItem key={presetKey(o)} value={presetKey(o)}>
            {presetLabel(o)}
          </MenuItem>
        ))}
        {value && !known ? <MenuItem value={value}>{value}</MenuItem> : null}
      </Select>
    </FormControl>
  );
}

export default function Editor({ config }) {
  const { id } = useParams();
  const routerHistory = useHistory();
  const { enqueueSnackbar } = useSnackbar();
  const metaDataValue = useSelector((state) => state.metaData);
  const reportQuery = useGetReportQuery(
    { token: window.localStorage.getItem("token"), id },
    { skip: !id, refetchOnMountOrArgChange: true }
  );
  const [createReport, createResult] = useCreateReportMutation();
  const [updateReport, updateResult] = useUpdateReportMutation();

  const [form, setForm] = useState(() => (id ? null : newForm(config, browserZone())));
  const [errors, setErrors] = useState({});
  const [problem, setProblem] = useState(null);
  const saving = createResult.isLoading || updateResult.isLoading;
  // what the form was filled from: decides the update body and the unsaved-changes guard
  const base = useRef(null);
  const baseJson = useRef(form ? JSON.stringify(form) : null);
  const needsSeed = useRef(!!id);
  const leaving = useRef(false);

  // Filled once from a fresh read, and again only when the user asks for Reload after a 409; a token
  // renewal re-reads the report and must not touch the form.
  useEffect(() => {
    const report = reportQuery.data && reportQuery.data.payload && reportQuery.data.payload.report;
    if (!needsSeed.current || !report || reportQuery.isFetching || reportQuery.error) return;
    needsSeed.current = false;
    const seeded = formFromReport(report);
    base.current = report;
    baseJson.current = JSON.stringify(seeded);
    setForm(seeded);
    setErrors({});
    setProblem(null);
  }, [reportQuery.data, reportQuery.isFetching, reportQuery.error]);

  const catalogue = config.sections || [];
  const features = form ? sectionFeatures(form.sections, catalogue) : [];
  const offered = useMemo(
    () => solutionsOffered(metaDataValue, features),
    [metaDataValue, features.join()]
  );

  if (!form) {
    if (reportQuery.error) {
      return (
        <Card style={{ padding: "40px 20px", textAlign: "center" }}>
          <p style={{ fontSize: "15px", color: "#c7c7c7", margin: "0 0 12px 0" }}>{classifyReportError(reportQuery.error).message}</p>
          <Button component={Link} to="/scheduledReports/list" color="secondary">
            Back to the reports
          </Button>
        </Card>
      );
    }
    return <Loader />;
  }

  const set = (patch) => {
    setForm((old) => ({ ...old, ...patch }));
    setErrors({});
    setProblem((old) => (old && old.kind === "validation" ? null : old));
  };
  const dirty = JSON.stringify(form) !== baseJson.current;
  const presets = config.presets || [];
  const objectives = config.objectivePresets || [];
  const stored = base.current;

  // The body for preview and test send, or null with the fields marked when the form is not ready.
  const draftBody = () => {
    const found = validateForm(form, config);
    setErrors(found);
    return Object.keys(found).length ? null : buildDraft(form, stored);
  };

  const onSaveFailed = (error) => {
    const classified = classifyReportError(error);
    setProblem(classified);
    setErrors(classified.fieldErrors || {});
  };

  const save = async () => {
    const found = validateForm(form, config);
    setErrors(found);
    if (Object.keys(found).length) {
      setProblem({ kind: "validation", message: "Please correct the marked fields." });
      return;
    }
    setProblem(null);
    const token = window.localStorage.getItem("token");
    const body = buildBody(form, stored);
    const res = id ? await updateReport({ token, id, body }) : await createReport({ token, body });
    if (res.error) {
      onSaveFailed(res.error);
      return;
    }
    leaving.current = true;
    showSnackbar("Scheduled Reports", "Report saved.", "success", 2000, enqueueSnackbar);
    const warnings = (res.data && res.data.payload && res.data.payload.warnings) || [];
    if (warnings.length) {
      const lines = warnings.slice(0, 3).map((w) => `${w.address}: ${w.message}.`);
      if (warnings.length > 3) lines.push(`And ${warnings.length - 3} more.`);
      showSnackbar("Scheduled Reports", lines.join(" "), "warning", 8000, enqueueSnackbar);
    }
    routerHistory.push("/scheduledReports/list");
  };

  const reload = () => {
    needsSeed.current = true;
    reportQuery.refetch();
  };

  const deniedIds = (problem && problem.serviceIds) || [];
  const nameOf = (serviceId) =>
    (metaDataValue.services || []).find((s) => String(s.id) === String(serviceId))?.name || "a solution you cannot see";

  return (
    <div>
      <Prompt when={dirty} message={() => (leaving.current ? true : LEAVE_MESSAGE)} />
      <div style={{ display: "flex", alignItems: "center", gap: "8px", marginBottom: "16px" }}>
        <p style={{ fontSize: "17px", fontWeight: "bold", margin: 0, flex: 1 }}>{id ? "Edit report" : "New report"}</p>
        <Button component={Link} to="/scheduledReports/list" color="error" disabled={saving}>
          Cancel
        </Button>
        <Button variant="contained" color="secondary" onClick={save} disabled={saving}>
          {saving ? <CircularProgress size={20} color="inherit" /> : "Save"}
        </Button>
      </div>
      {problem ? (
        <Alert
          severity={problem.kind === "conflict" ? "warning" : "error"}
          style={{ marginBottom: "16px" }}
          action={
            problem.kind === "conflict" ? (
              <Button color="inherit" size="small" onClick={reload} disabled={reportQuery.isFetching}>
                Reload report
              </Button>
            ) : null
          }
        >
          {problem.message}
          {deniedIds.length ? ` This is about: ${deniedIds.map(nameOf).join(", ")}.` : ""}
          {(errors._other || []).map((line) => (
            <div key={line}>{line}</div>
          ))}
        </Alert>
      ) : null}
      <Grid container spacing={2} alignItems="flex-start">
        <Grid item xs={7}>
          <Block title="Report">
            <TextField
              size="small"
              fullWidth
              label="Name"
              value={form.name}
              onChange={(e) => set({ name: e.target.value })}
              error={!!fieldError(errors, "name")}
              helperText={fieldError(errors, "name") || " "}
            />
            {presets.length ? (
              <div style={{ marginTop: "8px" }}>
                <PresetSelect
                  label="Preset"
                  value={form.preset}
                  options={presets}
                  onChange={(key) => {
                    const preset = presets.find((p) => presetKey(p) === key);
                    if (preset) set(applyPreset(form, preset));
                    else set({ preset: key });
                  }}
                />
              </div>
            ) : null}
            {objectives.length ? (
              <div style={{ marginTop: "16px" }}>
                <PresetSelect
                  label="What the report puts first"
                  value={form.objectivePreset}
                  options={objectives}
                  onChange={(objectivePreset) => set({ objectivePreset })}
                />
              </div>
            ) : null}
          </Block>
          <Block title="Solutions">
            <ScopePicker
              scope={form.scope}
              onChange={(scope) => set({ scope })}
              offered={offered}
              services={metaDataValue.services}
              canAll={metaDataValue.unscopedAdmin || (stored && stored.scope && stored.scope.mode === "all")}
              deniedIds={deniedIds.map(String)}
              error={fieldError(errors, "scope")}
            />
          </Block>
          <Block title="Sections">
            <SectionPicker sections={form.sections} catalogue={catalogue} onChange={(sections) => set({ sections })} errors={errors} />
          </Block>
          <Block title="Schedule">
            <SchedulePicker schedule={form.schedule} onChange={(schedule) => set({ schedule })} limits={config.limits} errors={errors} />
          </Block>
          <Block title="Recipients">
            <RecipientList
              recipients={form.recipients}
              onChange={(recipients) => set({ recipients })}
              scope={form.scope}
              sections={form.sections}
              reportId={id}
              errors={errors}
            />
          </Block>
        </Grid>
        <Grid item xs={5}>
          <PreviewPane draftBody={draftBody} onFieldErrors={setErrors} />
        </Grid>
      </Grid>
    </div>
  );
}
